import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { Small } from "@/src/components/typography";
import { cn } from "@/src/lib/shadcn-utils";

const HEADER_VARIANT_CLASS = {
  outline: "bg-muted/40",
  success: "border-success bg-success text-success-foreground",
  warn: "border-warn bg-warn text-warn-foreground",
  destructive: "border-destructive bg-destructive text-destructive-foreground",
} as const;

const CARD_VARIANT_CLASS = {
  outline: "",
  success: "border-success",
  warn: "border-warn",
  destructive: "border-destructive",
} as const;

export type ValerieCardHeaderVariant = keyof typeof HEADER_VARIANT_CLASS;

/**
 * Standard Valerie card: consistent header (muted icon + base-size title on the left, optional
 * actions slot on the right) divided from the content by a bottom border, so cards across the
 * app line up visually.
 *
 * Keep it dumb: callers own everything inside `children` and `actions`. Prefer this over raw
 * Card/CardHeader composition when a card has a titled header.
 */
export function ValerieCard({
  title,
  icon,
  actions,
  className,
  variant = "outline",
  contentClassName,
  children,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  variant?: ValerieCardHeaderVariant;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn("overflow-hidden", CARD_VARIANT_CLASS[variant], className)}>
      <CardHeader
        className={cn(
          "flex-row items-center justify-between gap-2 space-y-0 border-b py-2.5",
          HEADER_VARIANT_CLASS[variant]
        )}
      >
        <CardTitle className="flex min-w-0 items-center gap-2 font-sans font-medium tracking-normal">
          {icon ? (
            <span
              className={cn(
                "shrink-0 [&>svg]:h-4 [&>svg]:w-4",
                variant === "outline" ? "text-muted-foreground" : "text-current opacity-90"
              )}
            >
              {icon}
            </span>
          ) : null}
          <Small className="block min-w-0 truncate font-sans">{title}</Small>
        </CardTitle>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent className={cn("pt-3", contentClassName)}>{children}</CardContent>
    </Card>
  );
}
